import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from '../../components/ScrollX';
import WorkDone from './WorkDone';
import { supabase } from '../../supabase';
import { pageAll } from '../../utils/pageAll';
import { TASK_STATUS_ON_US } from '../../constants/statuses';   // WAITING is now bucketed in SQL (staff_workload_counts)

// Productivity report (admin-only; Live Reporting shows the card to admins only — registry `adminOnly`, step 8 part 6). Per active staffer, over a date range:
//   - Completed (credited to completed_by_id, completed_at in range)
//   - Met / Missed / No deadline (completed-in-range vs the FINAL due_date)
//   - Beat original (completed-in-range vs original_due_date) — shown as beat / known, and tasks with NO known original
//     are EXCLUDED (never fabricated), with the excluded count surfaced (STRICT: pre-column tasks have null original).
//   - Deadline moved (completed-in-range where original_due_date differs from due_date; only meaningful when original known)
//   - On us / Waiting (AS OF TODAY — not the range) now come from the staff_workload_counts() RPC, the SAME source as
//     the Team Workload roster and the unfiltered All-Tasks KPI cards. ⛔ WHY AN RPC: this screen reads `tasks` under
//     the Batch-2 confidential-circle policy, so a direct query silently OMITS other people's confidential tasks and
//     reports a busy person as idle. The RPC is SECURITY DEFINER and counts them without exposing anything but
//     integers. Buckets are the same ON_US / WAITING split as before.
//   - ⛔ ATTRIBUTION CHANGED (2026-08-15) AND NOW DIFFERS FROM THIS REPORT'S COMPLETED COLUMNS. The RPC folds PTO
//     coverage: an actively-covered task counts ONCE, for the COVERER, not the owner — so On us / Waiting are
//     CAPACITY (who is carrying the work today), matching the roster. The completed/met/missed/moved columns are
//     unchanged and remain OWNERSHIP (credited via completed_by_id). Both are intentional; they answer different
//     questions. The roster carries the mirror note (TaskEngine.js, workloadRoster).
//   - Overdue is still computed HERE from a direct query, so it still omits confidential tasks — the roster has the
//     same gap for the same reason (_displayStatus has no SQL equivalent). See the migration header,
//     supabase/migrations/2026-08-15_staff_workload_counts.sql.
// COUNTS ONLY — no task titles are ever fetched, so confidential tasks appear purely as numbers with no drill surface.
// Queries page past the 1000-row cap from the start.

// ── Local-time date helpers (project rule: never toISOString for date-only logic; parse 'YYYY-MM-DD' as LOCAL midnight).
const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;   // Date → 'YYYY-MM-DD' (local)
const localMid = (str) => { const [y, m, d] = str.split('-').map(Number); return new Date(y, m - 1, d); };   // local midnight
const todayMid = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); };
// The local calendar date a timestamp falls on (completion day), as a local-midnight Date.
const tsLocalMid = (ts) => { const d = new Date(ts); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };

// Preset ranges → { start, end } as inclusive 'YYYY-MM-DD' local dates.
function presetRange(key) {
  const n = new Date();
  const y = n.getFullYear(), m = n.getMonth();
  switch (key) {
    case 'this_month':  return { start: fmt(new Date(y, m, 1)),       end: fmt(new Date(y, m + 1, 0)) };
    case 'last_month':  return { start: fmt(new Date(y, m - 1, 1)),   end: fmt(new Date(y, m, 0)) };
    case 'this_quarter': { const q = Math.floor(m / 3) * 3; return { start: fmt(new Date(y, q, 1)), end: fmt(new Date(y, q + 3, 0)) }; }
    case 'this_year':   return { start: fmt(new Date(y, 0, 1)),       end: fmt(new Date(y, 11, 31)) };
    case 'last_30':     { const t = todayMid(); const s = new Date(t); s.setDate(s.getDate() - 29); return { start: fmt(s), end: fmt(t) }; }
    default:            return { start: fmt(new Date(y, m, 1)),       end: fmt(new Date(y, m + 1, 0)) };
  }
}

export default function ProductivityReport({ darkMode, staffUser }) {
  const isDark = darkMode;
  const c = isDark
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.03)' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)' };

  const init = presetRange('this_month');
  const [start, setStart] = useState(init.start);
  const [end, setEnd] = useState(init.end);
  const [preset, setPreset] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);              // per-staff aggregates, sorted
  const [excludedNoOriginal, setExcludedNoOriginal] = useState(0);   // completed-in-range with NO known original (excluded from beat)
  const [workloadErr, setWorkloadErr] = useState('');                // staff_workload_counts() failed → On us / Waiting show '—', never 0

  const applyPreset = (key) => { const r = presetRange(key); setPreset(key); setStart(r.start); setEnd(r.end); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Active staff only (matches the Team Workload roster). Departed staff (active === false) are excluded as noise.
      const { data: staffData } = await supabase.from('ntrg_staff').select('id, name, active').order('name');
      const staff = (staffData || []).filter(s => s.active !== false);

      // Range as instants for the timestamptz completed_at filter: [local midnight of start, local midnight of end+1day).
      const startInstant = localMid(start);                                   // local midnight of start
      const endExclusive = new Date(localMid(end).getTime()); endExclusive.setDate(endExclusive.getDate() + 1);
      const startISO = startInstant.toISOString();
      const endISO = endExclusive.toISOString();

      // Completed in range — minimal columns, NO titles. Paged past the cap.
      const completed = await pageAll(() => supabase.from('tasks')
        .select('completed_by_id, completed_at, due_date, original_due_date')
        .eq('status', 'Complete').is('deleted_at', null)
        .gte('completed_at', startISO).lt('completed_at', endISO));

      // Open snapshot as of today — non-terminal, by assigned_to. Paged past the cap.
      const open = await pageAll(() => supabase.from('tasks')
        .select('assigned_to, status, due_date')
        .is('deleted_at', null).not('status', 'in', '("Complete","Cancelled")'));

      const today = todayMid();
      const blank = () => ({ completed: 0, met: 0, missed: 0, noDeadline: 0, beat: 0, knownOriginal: 0, moved: 0, onUs: 0, waiting: 0, overdue: 0 });
      const agg = {};
      staff.forEach(s => { agg[s.id] = blank(); });

      let excluded = 0;
      completed.forEach(t => {
        const a = agg[t.completed_by_id];
        if (!a) return;   // completed by a non-active/departed staffer → outside the active roster
        a.completed += 1;
        const cMid = tsLocalMid(t.completed_at);
        if (!t.due_date) a.noDeadline += 1;
        else { const dueMid = localMid(t.due_date); if (cMid.getTime() <= dueMid.getTime()) a.met += 1; else a.missed += 1; }
        if (t.original_due_date) {
          a.knownOriginal += 1;
          const oMid = localMid(t.original_due_date);
          if (cMid.getTime() <= oMid.getTime()) a.beat += 1;
          if (t.due_date !== t.original_due_date) a.moved += 1;   // deadline changed (both known)
        } else {
          excluded += 1;   // no known original → excluded from beat-original (STRICT; never fabricated)
        }
      });

      // Overdue only — On us / Waiting come from the RPC below. This pass still sees ONLY the tasks RLS lets
      // this viewer read, so overdue keeps the confidential undercount (see the header note).
      open.forEach(t => {
        const a = agg[t.assigned_to];
        if (!a) return;
        const onUs = TASK_STATUS_ON_US.includes(t.status);
        if (onUs && t.due_date && localMid(t.due_date).getTime() < today.getTime()) a.overdue += 1;   // past-due ∩ ON_US
      });

      // On us / Waiting — staff_workload_counts() (SECURITY DEFINER): includes confidential tasks and folds
      // PTO coverage onto the coverer. On failure they stay null so the table shows '—' rather than a zero
      // that reads as "no work" — the same rule as the roster; never fall back to the RLS-limited query.
      const { data: wl, error: wlErr } = await supabase.rpc('staff_workload_counts');
      if (wlErr) {
        console.warn('[ProductivityReport] staff_workload_counts failed:', wlErr);
        Object.values(agg).forEach(a => { a.onUs = null; a.waiting = null; });
        setWorkloadErr(wlErr.message || 'unavailable');
      } else {
        setWorkloadErr('');
        (wl || []).forEach(r => {
          const a = agg[r.staff_id];
          if (!a) return;   // departed/unassigned bucket — outside the active roster, same rule as completed
          // Unchanged meanings on purpose: the RPC's buckets are EXCLUSIVE (overdue split out), so On us
          // re-adds the overdue slices. Week-over-week comparisons in this report stay valid — the KPI
          // cards present the same row as exclusive buckets; this report does not.
          a.onUs = r.todo + r.in_progress + r.overdue_todo + r.overdue_in_progress;
          a.waiting = r.waiting;
        });
      }

      const out = staff.map(s => ({ id: s.id, name: s.name, ...agg[s.id] }))
        .sort((x, y) => (y.completed - x.completed) || x.name.localeCompare(y.name));
      setRows(out);
      setExcludedNoOriginal(excluded);
    } catch (e) {
      console.warn('[ProductivityReport] load failed:', e);
      setRows([]); setExcludedNoOriginal(0);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => {
    // ?? 0 — onUs/waiting are null when the RPC failed; the column totals render '—' in that case anyway.
    ['completed', 'met', 'missed', 'noDeadline', 'beat', 'knownOriginal', 'moved', 'onUs', 'waiting', 'overdue'].forEach(k => { acc[k] += (r[k] ?? 0); });
    return acc;
  }, { completed: 0, met: 0, missed: 0, noDeadline: 0, beat: 0, knownOriginal: 0, moved: 0, onUs: 0, waiting: 0, overdue: 0 });

  const th = { textAlign: 'center', padding: '8px 10px', fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' };
  const thL = { ...th, textAlign: 'left' };
  const td = { textAlign: 'center', padding: '7px 10px', fontSize: '13px', color: c.text, borderBottom: `1px solid ${c.border}` };
  const tdL = { ...td, textAlign: 'left', fontWeight: 600 };
  const presetBtn = (key, label) => (
    <button key={key} onClick={() => applyPreset(key)} style={{
      padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
      border: preset === key ? '1px solid #B40000' : `1px solid ${c.border}`, background: preset === key ? 'rgba(180,0,0,0.15)' : 'transparent', color: preset === key ? 'var(--red)' : c.muted,
    }}>{label}</button>
  );

  return (
    <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Per person, completions and deadline performance over a date range. Open / Overdue are <strong>as of today</strong>. Counts only.</div>

      {/* Range presets + custom inputs */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
        {presetBtn('this_month', 'This month')}
        {presetBtn('last_month', 'Last month')}
        {presetBtn('this_quarter', 'This quarter')}
        {presetBtn('this_year', 'This year')}
        {presetBtn('last_30', 'Last 30 days')}
        <span style={{ width: '1px', height: '22px', background: c.border, margin: '0 4px' }} />
        <input type="date" value={start} onChange={e => { setPreset('custom'); setStart(e.target.value); }}
          style={{ padding: '5px 8px', borderRadius: '7px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '12px' }} />
        <span style={{ color: c.muted, fontSize: '12px' }}>to</span>
        <input type="date" value={end} onChange={e => { setPreset('custom'); setEnd(e.target.value); }}
          style={{ padding: '5px 8px', borderRadius: '7px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '12px' }} />
      </div>

      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <ScrollX style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr>
                <th style={thL}>Staffer</th>
                <th style={th} title="Tasks this person completed, with completion date in range">Completed</th>
                <th style={th} title="Completed on/before the final due date">Met</th>
                <th style={th} title="Completed after the final due date">Missed</th>
                <th style={th} title="Completed tasks that had no due date">No deadline</th>
                <th style={th} title="Completed on/before the ORIGINAL due date — shown as beat / (tasks with a known original)">Beat orig.</th>
                <th style={th} title="Completed tasks whose deadline was changed from its original (only counted when the original is known)">Moved</th>
                <th style={th} title="On-us tasks assigned to this person (TO DO + IN PROGRESS), as of today — matches the roster's active">On us</th>
                <th style={th} title="Waiting-on-them tasks assigned to this person (client/consultant), as of today — matches the roster's waiting">Waiting</th>
                <th style={th} title="Past-due and still on us (on-us statuses only), as of today — matches the KPI cards">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: c.muted, padding: '28px' }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: c.muted, padding: '28px' }}>No active staff.</td></tr>
              ) : rows.map(r => {
                const zero = !r.completed && !r.onUs && !r.waiting && !r.overdue;   // null (RPC failed) is falsy — fine
                return (
                  <tr key={r.id} style={{ opacity: zero ? 0.55 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tdL}>{r.name}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.completed}</td>
                    <td style={{ ...td, color: r.met ? '#34d399' : c.muted }}>{r.met}</td>
                    <td style={{ ...td, color: r.missed ? '#f87171' : c.muted }}>{r.missed}</td>
                    <td style={{ ...td, color: c.muted }}>{r.noDeadline}</td>
                    <td style={td} title={`${r.beat} of ${r.knownOriginal} completed tasks with a known original deadline`}>{r.knownOriginal ? `${r.beat} / ${r.knownOriginal}` : '—'}</td>
                    <td style={{ ...td, color: c.muted }}>{r.moved}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.onUs ?? '—'}</td>
                    <td style={{ ...td, color: c.muted }}>{r.waiting ?? '—'}</td>
                    <td style={{ ...td, color: r.overdue ? '#f87171' : c.muted, fontWeight: r.overdue ? 700 : 400 }}>{r.overdue}</td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot>
                <tr style={{ borderTop: `2px solid ${c.border}` }}>
                  <td style={{ ...tdL, fontWeight: 700 }}>Total</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.completed}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.met}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.missed}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.noDeadline}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.knownOriginal ? `${totals.beat} / ${totals.knownOriginal}` : '—'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.moved}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{workloadErr ? '—' : totals.onUs}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{workloadErr ? '—' : totals.waiting}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{totals.overdue}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </ScrollX>
      </div>

      {!loading && (
        <div style={{ fontSize: '11px', color: c.muted, marginTop: '10px', lineHeight: 1.6 }}>
          <strong>Beat orig.</strong> counts completions on/before the task's <em>original</em> due date, shown as <em>beat / tasks-with-a-known-original</em>.{' '}
          {totals.completed === 0
            ? 'No completed tasks in this range yet.'
            : excludedNoOriginal > 0
              ? <><strong>{excludedNoOriginal}</strong> completed task{excludedNoOriginal === 1 ? '' : 's'} in this range {excludedNoOriginal === 1 ? 'has' : 'have'} no known original deadline (created before original-deadline tracking) and {excludedNoOriginal === 1 ? 'is' : 'are'} excluded from Beat orig.</>
              : 'All completed tasks in this range have a known original deadline.'}
          {' '}On us / Waiting / Overdue are as of today, not the selected range. <strong>On us / Waiting follow PTO coverage</strong> — an actively-covered task counts for the coverer, not the owner, matching the Team Workload roster; the completed columns still credit the owner. Confidential tasks are included as counts only (Overdue excepted — it still omits them).
        </div>
      )}
      {!loading && workloadErr && (
        <div style={{ fontSize: '11px', color: '#f87171', marginTop: '8px' }}>
          ⚠ On us / Waiting unavailable — {workloadErr}. Shown as “—” rather than a zero that would read as no work.
        </div>
      )}
      <WorkDone start={start} end={end} staffUser={staffUser} c={c} />
    </div>
  );
}
